import Card, { CardBody } from '@airwallex/card';
import { formatCurrency } from '@airwallex/awx-currency-formatter';
import { formatMoney } from '@/utils/formatPayment';
import { Text } from '@airwallex/typography';
import React, { useContext } from 'react';
import { HStack, VStack } from '@airwallex/stacks';
import TotalSection from './TotalSection';
import Metadata from './Metadata';
import { useTranslation } from 'react-i18next';
import { formatDate, DATE_PRECISION, DATE_STYLE } from '@/utils/formatDate';
import { useTabletOrMobile } from '@/hooks/useMediaQuery';
import { StoreContext } from '@/providers/storeProvider';
import AutomaticCurrencyConversion from './AutomaticCurrencyConversion';
import { spacing } from '@airwallex/global';
import i18n from '@/utils/i18n';
import DownloadButton from './Download';

const Summary: React.FC = () => {
  const { t } = useTranslation();
  const { invoiceData, quote } = useContext(StoreContext);
  const { invoice } = invoiceData;
  const {
    totalAmount,
    totalDueAmount,
    currency,
    amountRemaining,
    creditNoteAmountBeforePayment,
    creditNoteAmountAfterPayment,
    paymentsAmount,
  } = invoice;
  const { dueAt } = invoice;
  const isCurrencySwitcherEnabled = false;
  const isTabletOrMobile = useTabletOrMobile();

  const summaryTotals = [
    {
      label: t('invoiceTotal'),
      value: formatMoney(totalAmount ?? 0, currency),
    },
    ...(creditNoteAmountBeforePayment || creditNoteAmountAfterPayment
      ? [
          {
            label: t('creditApplied'),
            value: `-${formatMoney(
              (creditNoteAmountBeforePayment ?? 0) + (creditNoteAmountAfterPayment ?? 0),
              currency
            )}`,
          },
        ]
      : []),
    ...(paymentsAmount
      ? [
          {
            label: t('paymentReceived'),
            value: `-${formatMoney(paymentsAmount, currency)}`,
          },
        ]
      : []),
    ...(creditNoteAmountAfterPayment
      ? [
          {
            label: t('refundIssued'),
            value: formatMoney(creditNoteAmountAfterPayment, currency),
          },
        ]
      : []),
    {
      label: t('amountDue', { currency }),
      value: formatMoney(amountRemaining ?? 0, currency),
    },
  ];

  return (
    <Card style={{ width: '100%' }}>
      <CardBody>
        <VStack align="STRETCH" space="16px">
          <Metadata />
          <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <VStack align="STRETCH">
              <Text variant="body" style={{ marginBottom: spacing.s }}>
                {t('dueOn', {
                  dueDate: formatDate(dueAt, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY),
                })}
              </Text>
              <Text variant="display200">
                {formatCurrency({
                  amount: quote?.target_amount ?? totalDueAmount ?? 0,
                  currency: quote?.target_currency ?? currency,
                  showSymbol: true,
                  showCode: true,
                })}
              </Text>
              {isCurrencySwitcherEnabled && <AutomaticCurrencyConversion />}
            </VStack>
            {!isTabletOrMobile && (
              <div style={{ alignContent: 'flex-end' }}>
                <DownloadButton />
              </div>
            )}
          </HStack>
          {(paymentsAmount || creditNoteAmountBeforePayment) && <TotalSection totals={summaryTotals} />}
          {isTabletOrMobile && <DownloadButton />}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default Summary;
